/**
 * NewPatientsStep
 *
 * Fetches and displays a paginated table of unmatched (new) patients.
 */

import { useState } from 'react';
import { Alert, Box, Skeleton, TablePagination, Typography } from '@mui/material';

import { useGetReviewPhaseQuery } from '../../../store/hooks';
import type { NewPatientItem } from '../../../types';
import { PatientReviewTable } from '../components/PatientReviewTable';
import type { NewPatientsStepProps } from '../ImportReviewWizard.types';

const PAGE_SIZE = 100;

export const NewPatientsStep = ({ sessionId }: NewPatientsStepProps) => {
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching, error } = useGetReviewPhaseQuery({
    sessionId,
    phase: 'new_patients',
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={32} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Failed to load new patients. Please try again.</Alert>
    );
  }

  const patients = (data?.items as NewPatientItem[] | undefined) ?? [];
  const totalCount = data?.total_count ?? patients.length;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          New Patients
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {totalCount} patient{totalCount === 1 ? '' : 's'} will be created
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', opacity: isFetching ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        <PatientReviewTable patients={patients} rowOffset={page * PAGE_SIZE} />
      </Box>

      {totalCount > PAGE_SIZE && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      )}
    </Box>
  );
};
